import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { I18nextProvider } from "react-i18next";

import App from "@/App";
import PreviewModeBanner from "@/components/preview/PreviewModeBanner";
import i18n, { persistLanguageChoice } from "@/i18n";
import { AuthProvider } from "@/auth";
import { bootAnalytics } from "@/lib/analytics";
import { applyLanguageDirection, ensureFontsForLanguage } from "@/lib/fonts";
import { installErrorReporter } from "@/lib/error-reporter";
import { bootSentry } from "@/lib/sentry";
import { registerAppServiceWorker, shouldRegisterServiceWorker } from "@/sw-registration";
import "@/index.css";

/**
 * App entry point.
 *
 * Providers (outermost → innermost):
 *   BrowserRouter → QueryClientProvider → I18nextProvider → App
 *
 * Strict mode is deliberately enabled; @vitejs/plugin-react's fast-refresh handles
 * the double-render correctly in dev. If you see double API hits, that's React 18
 * strict-mode dev behavior — confirm with `NODE_ENV=production` before filing a bug.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Cross-site lang handoff: strip ?lang= from history now that i18n has read
// it, and persist the choice so subsequent visits stay in the same language.
const _handoffParams = new URLSearchParams(window.location.search);
if (_handoffParams.has("lang")) {
  persistLanguageChoice(i18n.language);
  _handoffParams.delete("lang");
  const _newSearch = _handoffParams.toString();
  window.history.replaceState(
    null,
    "",
    window.location.pathname + (_newSearch ? `?${_newSearch}` : "") + window.location.hash,
  );
}

// Initial language-driven side effects (dir + font preload).
applyLanguageDirection(i18n.language);
void ensureFontsForLanguage(i18n.language);
i18n.on("languageChanged", (lng) => {
  applyLanguageDirection(lng);
  void ensureFontsForLanguage(lng);
});

// Register the push service worker at root scope. Gated on secure-context +
// SW API support (see sw-registration.ts). Fire-and-forget; failures log but
// never block the app from rendering.
if (shouldRegisterServiceWorker()) {
  void registerAppServiceWorker();
}

// Boot the GlitchTip/Sentry SDK BEFORE the in-house error reporter +
// analytics + render. The SDK is silent without `VITE_GLITCHTIP_DSN`
// (dev default) — see `lib/sentry.ts`. When the DSN is set, spontaneous
// browser errors and unhandled rejections are captured directly to
// GlitchTip; the in-house `/api/errors` reporter remains as the
// belt-and-braces second sink (PII-scrubbed by the backend).
bootSentry();

// Wave 14k (ROADS-348) — install global error reporter BEFORE rendering so
// any error during initial render surfaces through the pipeline. The
// reporter is fail-soft; failures to POST /api/errors are silently dropped.
installErrorReporter();

// PostHog bootstrap. Silent no-op without VITE_POSTHOG_KEY (dev default)
// and self-gated on consent — capture only starts after onSignInConsent()
// fires inside AuthContext on a successful /auth/me. See
// `apps/web/src/lib/analytics.ts` for the ROADS-307 consent-model notes.
bootAnalytics();

const container = document.getElementById("root");
if (!container) throw new Error("Root container #root not found in index.html");

createRoot(container).render(
  <React.StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          {import.meta.env.VITE_PREVIEW_MODE === "true" && <PreviewModeBanner />}
          <AuthProvider>
            <App />
          </AuthProvider>
        </I18nextProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>
);
